import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.recheckPrice();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
      this.recheckPrice();
    }
  };

  /**
   * Rechecks the per-item price and updates it with higher precision if necessary.
   */
  recheckPrice = () => {
    const container = this.querySelector('[ref="priceContainer"]');
    if (!container || container.dataset.perItem !== 'true') return;

    const priceCents = parseFloat(container.dataset.priceCents);
    const totalCount = parseInt(container.dataset.totalCount);
    if (isNaN(priceCents) || isNaN(totalCount) || totalCount <= 0) return;

    const perItemPrice = priceCents / 100 / totalCount;
    const priceElement = container.querySelector('.price');
    if (!priceElement) return;

    if (perItemPrice > 0 && perItemPrice < 1.0) {
      let decimals = 2;
      const priceStr = perItemPrice.toFixed(10);
      const parts = priceStr.split('.');
      const afterDecimal = parts[1];

      if (afterDecimal.startsWith('00')) {
        for (let i = 2; i < afterDecimal.length; i++) {
          if (afterDecimal[i] !== '0') {
            decimals = i + 1;
            break;
          }
        }
      }

      const currentText = priceElement.textContent.trim();

      try {
        const locale = window.Shopify?.locale || 'en-US';
        const currency = window.Shopify?.currency?.active || 'USD';

        const formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });

        const truncatedAfterDecimal = afterDecimal.substring(0, decimals);
        const truncatedPrice = parseFloat(`${parts[0]}.${truncatedAfterDecimal}`);

        priceElement.textContent = formatter.format(truncatedPrice);
      } catch (e) {
        // Fallback if Intl fails
        const symbolMatch = currentText.match(/^[^\d,.]+/);
        const symbol = symbolMatch ? symbolMatch[0] : '$';

        const truncatedAfterDecimal = afterDecimal.substring(0, decimals);
        let formattedPrice = `${parts[0]}.${truncatedAfterDecimal}`;

        if (currentText.includes(',') && !currentText.includes('.')) {
          formattedPrice = formattedPrice.replace('.', ',');
        }
        priceElement.textContent = symbol + formattedPrice;
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
